import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { addDoc, collection, doc, setDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { LuBuilding2, LuFileText, LuLoaderCircle, LuPencil, LuPlus, LuSave, LuTriangleAlert } from 'react-icons/lu';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import Modal from '../../components/ui/Modal';
import SegmentedControl from '../../components/ui/SegmentedControl';
import { Field, TextInput } from '../../components/form/FormControls';
import { db } from '../../config/firebaseConfig';
import { useTranslation } from '../../i18n/LanguageContext';

const FORM_ID = 'customer-form';
const FIELD_SELECTOR = 'input:not([disabled])';
// Element ids of the fields (the Firestore names hold spaces and punctuation)
const FIELD_IDS = {
  'Müşteri Adı': 'name',
  'Bilgi': 'info',
  'Ana Anlaşma Numarası': 'agreement-number',
  'Ana Anlaşma Tarihi': 'agreement-date',
  'Müşteri Hizmet Num.': 'service-number',
  'Teslim Tarihi': 'end-date',
  'Vergi Numarası': 'tax-number',
  'Ödeme Periyodu': 'payment-period',
};
const fieldId = (name) => `customer-form-${FIELD_IDS[name]}`;

// Kayıttan form değerleri (eski davranış: ödeme periyodu yoksa 30 gün önerilir)
const toForm = (customer) => (customer ? {
  'Müşteri Adı': customer['Başlık'] || customer['Müşteri Adı'] || '',
  'Bilgi': customer['Bilgi'] || '',
  'Ana Anlaşma Numarası': customer['Ana Anlaşma Numarası'] || '',
  'Ana Anlaşma Tarihi': customer['Ana Anlaşma Tarihi'] || '',
  'Durum': customer['Durum'] || 'aktif',
  'Döviz Tipi': customer['Döviz Tipi'] || 'TRY',
  'Müşteri Hizmet Num.': customer['Müşteri Hizmet Num.'] || '',
  'Teslim Tarihi': customer['Teslim Tarihi'] || '',
  'Vergi Numarası': customer['Vergi Numarası'] || '',
  'Yurtiçi mi?': customer['Yurtiçi mi?'] || 'Evet',
  'Ödeme Periyodu': customer['Ödeme Periyodu'] || 30,
} : {
  'Müşteri Adı': '',
  'Bilgi': '',
  'Ana Anlaşma Numarası': '',
  'Ana Anlaşma Tarihi': '',
  'Durum': 'aktif',
  'Döviz Tipi': 'TRY',
  'Müşteri Hizmet Num.': '',
  'Teslim Tarihi': '',
  'Vergi Numarası': '',
  'Yurtiçi mi?': 'Evet',
  'Ödeme Periyodu': '',
});

const validate = (form, t) => {
  const errors = {};
  if (!form['Müşteri Adı'] || form['Müşteri Adı'].trim() === '') {
    errors['Müşteri Adı'] = t('customers.form.errors.nameRequired');
  }
  const period = form['Ödeme Periyodu'];
  if (period !== '' && period !== null && period !== undefined && (Number.isNaN(Number(period)) || Number(period) < 0)) {
    errors['Ödeme Periyodu'] = t('customers.form.errors.paymentInvalid');
  }
  return errors;
};

const SectionTitle = ({ icon: Icon, children }) => (
  <p className="tw-col-span-full tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500">
    <Icon aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
    {children}
  </p>
);

SectionTitle.propTypes = { icon: PropTypes.elementType.isRequired, children: PropTypes.node };

/**
 * "Yeni Müşteri Ekle" / "Müşteriyi Düzenle" (`customer` given). Every field
 * on one screen; the customer name is saved in capitals and also as
 * 'Başlık'. Enter moves to the next field, Ctrl/⌘+Enter saves; closing with
 * unsaved changes asks first.
 */
const EditCustomerModal = ({ customer, onClose, onSaved }) => {
  const { t, language } = useTranslation();
  const isEdit = Boolean(customer);
  const initial = useMemo(() => toForm(customer), [customer]);
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const formRef = useRef(null);
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);

  const setField = (name, value) => {
    // Müşteri adı büyük harfe çevrilir (eski davranış)
    const processed = name === 'Müşteri Adı' ? value.toUpperCase() : value;
    setForm((prev) => ({ ...prev, [name]: processed }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const requestClose = () => {
    if (saving) return;
    if (dirty) setConfirmingClose(true);
    else onClose();
  };

  // A stored value outside the choices stays selectable (and is kept)
  const choices = (name, options) => {
    const current = form[name];
    if (!current || options.some((option) => option.value === current)) return options;
    return [...options, { value: current, label: t('dataTable.form.outOfList', { value: current }) }];
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const found = validate(form, t);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      toast.error(t('customers.form.toast.fixErrors'));
      return;
    }

    setSaving(true);
    const now = new Date().toISOString();
    try {
      if (isEdit) {
        const customerData = { ...form, 'Başlık': form['Müşteri Adı'], 'Güncelleme Tarihi': now };
        await setDoc(doc(db, 'Customers', customer.id), customerData, { merge: true });
        await onSaved();
        toast.success(t('customers.form.toast.updated'));
      } else {
        const customerData = { ...form, 'Başlık': form['Müşteri Adı'], 'Oluşturma Tarihi': now, 'Güncelleme Tarihi': now };
        const ref = await addDoc(collection(db, 'Customers'), customerData);
        await onSaved(ref.id);
        toast.success(t('customers.form.toast.added'));
      }
      onClose();
    } catch (error) {
      console.error(isEdit ? 'Error updating customer:' : 'Error adding new customer:', error);
      toast.error(t(isEdit ? 'customers.form.toast.updateError' : 'customers.form.toast.addError', { message: error.message }));
      setSaving(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      formRef.current?.requestSubmit();
      return;
    }
    if (event.target.tagName !== 'INPUT') return;
    // Enter: next field; on the last one, save
    event.preventDefault();
    const fields = Array.from(formRef.current.querySelectorAll(FIELD_SELECTOR));
    const next = fields[fields.indexOf(event.target) + 1];
    if (next) next.focus();
    else formRef.current.requestSubmit();
  };

  const text = (name, props = {}) => (
    <TextInput
      id={fieldId(name)}
      value={form[name]}
      onChange={(event) => setField(name, event.target.value)}
      error={errors[name]}
      {...props}
    />
  );

  const footer = confirmingClose ? (
    <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-2">
      <p className="tw-mr-auto tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-amber-700 dark:tw-text-amber-300">
        <LuTriangleAlert aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0" />
        {t('dataTable.form.unsaved')}
      </p>
      <Button variant="ghost" onClick={() => setConfirmingClose(false)}>{t('dataTable.form.keepEditing')}</Button>
      <Button variant="danger" onClick={onClose}>{t('dataTable.form.discard')}</Button>
    </div>
  ) : (
    <div className="tw-flex tw-w-full tw-items-center tw-gap-2">
      <p className="tw-mr-auto tw-hidden tw-text-xs tw-text-slate-400 md:tw-block dark:tw-text-slate-500">{t('customers.form.shortcut')}</p>
      <Button variant="ghost" onClick={requestClose} disabled={saving} className="max-sm:tw-hidden">{t('customers.common.cancel')}</Button>
      <Button variant="primary" type="submit" form={FORM_ID} disabled={saving} className="max-sm:tw-flex-1">
        {saving
          ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
          : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
        {saving ? t('customers.common.saving') : t(isEdit ? 'customers.form.submitEdit' : 'customers.form.submitAdd')}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen
      onClose={requestClose}
      size="form"
      title={t(isEdit ? 'customers.form.editTitle' : 'customers.form.addTitle')}
      subtitle={isEdit ? initial['Müşteri Adı'] : t('customers.form.addSubtitle')}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-indigo-400 tw-to-violet-600 tw-shadow-violet-500/30" size="xs">
          {isEdit ? <LuPencil aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" /> : <LuPlus aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />}
        </GlassIcon>
      )}
      footer={footer}
    >
      <form ref={formRef} id={FORM_ID} noValidate onSubmit={handleSubmit} onKeyDown={handleKeyDown} className="tw-space-y-6">
        <div className="tw-grid tw-gap-x-4 tw-gap-y-4 sm:tw-grid-cols-2 lg:tw-grid-cols-3">
          <SectionTitle icon={LuBuilding2}>{t('customers.form.company')}</SectionTitle>
          <Field label={t('customers.form.fields.name')} htmlFor={fieldId('Müşteri Adı')} required error={errors['Müşteri Adı']} hint={t('customers.form.fields.nameHint')} className="sm:tw-col-span-2">
            {text('Müşteri Adı', { autoComplete: 'off', autoCapitalize: 'characters' })}
          </Field>
          <Field label={t('customers.form.fields.status')}>
            <SegmentedControl
              options={choices('Durum', [
                { value: 'aktif', label: t('customers.status.active') },
                { value: 'inaktif', label: t('customers.status.inactive') },
              ])}
              value={form['Durum']}
              onChange={(value) => setField('Durum', value)}
              layoutId="customer-form-status"
              label={t('customers.form.fields.status')}
              className="tw-w-full [&>button]:tw-flex-1"
            />
          </Field>
          <Field label={t('customers.form.fields.info')} htmlFor={fieldId('Bilgi')} className="sm:tw-col-span-2">
            {text('Bilgi')}
          </Field>
          <Field label={t('customers.form.fields.domestic')}>
            <SegmentedControl
              options={choices('Yurtiçi mi?', [
                { value: 'Evet', label: t('customers.yes') },
                { value: 'Hayır', label: t('customers.no') },
              ])}
              value={form['Yurtiçi mi?']}
              onChange={(value) => setField('Yurtiçi mi?', value)}
              layoutId="customer-form-domestic"
              label={t('customers.form.fields.domestic')}
              className="tw-w-full [&>button]:tw-flex-1"
            />
          </Field>
          <Field label={t('customers.form.fields.taxNumber')} htmlFor={fieldId('Vergi Numarası')}>
            {text('Vergi Numarası', { inputMode: 'numeric', autoComplete: 'off' })}
          </Field>
          <Field label={t('customers.form.fields.serviceNumber')} htmlFor={fieldId('Müşteri Hizmet Num.')}>
            {text('Müşteri Hizmet Num.', { type: 'tel', autoComplete: 'off' })}
          </Field>
        </div>

        <div className="tw-grid tw-gap-x-4 tw-gap-y-4 sm:tw-grid-cols-2 lg:tw-grid-cols-3">
          <SectionTitle icon={LuFileText}>{t('customers.form.agreement')}</SectionTitle>
          <Field label={t('customers.form.fields.agreementNumber')} htmlFor={fieldId('Ana Anlaşma Numarası')}>
            {text('Ana Anlaşma Numarası', { autoComplete: 'off' })}
          </Field>
          <Field label={t('customers.form.fields.agreementDate')} htmlFor={fieldId('Ana Anlaşma Tarihi')}>
            {text('Ana Anlaşma Tarihi', { type: 'date' })}
          </Field>
          <Field label={t('customers.form.fields.endDate')} htmlFor={fieldId('Teslim Tarihi')} hint={t('customers.form.fields.endDateHint')}>
            {text('Teslim Tarihi', { type: 'date' })}
          </Field>
          <Field label={t('customers.form.fields.currency')}>
            <SegmentedControl
              options={choices('Döviz Tipi', ['TRY', 'USD', 'EUR'].map((code) => ({ value: code, label: code })))}
              value={form['Döviz Tipi']}
              onChange={(value) => setField('Döviz Tipi', value)}
              layoutId="customer-form-currency"
              label={t('customers.form.fields.currency')}
              className="tw-w-full [&>button]:tw-flex-1"
            />
          </Field>
          <Field
            label={t('customers.form.fields.paymentPeriod')}
            htmlFor={fieldId('Ödeme Periyodu')}
            error={errors['Ödeme Periyodu']}
            hint={t('customers.form.fields.paymentPeriodHint')}
          >
            {text('Ödeme Periyodu', { inputMode: 'numeric', autoComplete: 'off' })}
          </Field>
        </div>
      </form>
    </Modal>
  );
};

EditCustomerModal.propTypes = {
  customer: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default EditCustomerModal;
